import { useState } from "react";
import { cn } from "@/lib/utils";
import { DEFAULT_BRAND_LOGO, getBrandLogo } from "@/lib/brandLogo";

type Variant = "fullscreen" | "section" | "inline";

const SIZES: Record<Variant, { ring: number; logo: number; text: string }> = {
  fullscreen: { ring: 96, logo: 56, text: "text-sm" },
  section: { ring: 64, logo: 38, text: "text-sm" },
  inline: { ring: 28, logo: 16, text: "text-xs" },
};

/**
 * The one loading indicator for the whole app: the institution's logo (or the platform's until it's
 * known) inside a spinning ring tinted with the brand color. `fullscreen` covers the page (app/session
 * start), `section` centers in the content area (page/panel loading), `inline` is a compact row.
 * Button/action spinners stay as they are — this is for "the content isn't here yet".
 */
export function BrandedLoader({
  variant = "section",
  label = "Loading…",
  className,
}: {
  variant?: Variant;
  label?: string;
  className?: string;
}) {
  const { ring, logo, text } = SIZES[variant];
  const [src, setSrc] = useState(getBrandLogo);
  const r = ring / 2 - 3;

  const mark = (
    <div className="relative shrink-0" style={{ width: ring, height: ring }}>
      <svg className="absolute inset-0 animate-spin [animation-duration:1.4s]" width={ring} height={ring} viewBox={`0 0 ${ring} ${ring}`} fill="none" aria-hidden>
        <circle cx={ring / 2} cy={ring / 2} r={r} stroke="currentColor" strokeWidth={variant === "inline" ? 2 : 3} className="text-brand-neutral-200" />
        <circle
          cx={ring / 2}
          cy={ring / 2}
          r={r}
          stroke="currentColor"
          strokeWidth={variant === "inline" ? 2 : 3}
          strokeLinecap="round"
          strokeDasharray={2 * Math.PI * r}
          strokeDashoffset={2 * Math.PI * r * 0.7}
          className="text-brand-orange"
        />
      </svg>
      <img
        src={src}
        alt=""
        onError={() => src !== DEFAULT_BRAND_LOGO && setSrc(DEFAULT_BRAND_LOGO)}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full object-contain"
        style={{ width: logo, height: logo }}
      />
    </div>
  );

  if (variant === "inline") {
    return (
      <div role="status" aria-live="polite" className={cn("flex items-center gap-2 py-2 text-text-muted-dash", text, className)}>
        {mark}
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col items-center justify-center gap-3 text-text-muted-dash",
        variant === "fullscreen" ? "fixed inset-0 z-[100] bg-background" : "py-12",
        className
      )}
    >
      {mark}
      <span className={text}>{label}</span>
    </div>
  );
}
